
import { Table, Button, InputNumber, Input, Card, Col, Form, Row, Space, Popconfirm } from 'antd'
import { PlusOutlined, DeleteOutlined, CloseOutlined, RollbackOutlined } from '@ant-design/icons';

import { ButtonBack, ButtonSubmit } from 'components/Common/Buttons';
import useAccessPermissionHook from 'hooks/useAccessPermissionHook';
import useTopbarTitleHooks from 'hooks/useTopbarTitleHooks';
import { useAtom } from 'jotai';
import React, { useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom';
import { APP_ROUTE_PREFIX, RouteConstant } from 'routes/routesConstant';
import useOrgRepo from 'services/repo/useOrgRepo';
import { INIT_ORG, organizationAtom } from 'store/Org/org';
import { PRIVILEGE_ID } from 'utils/AccessPermissionConfig';
import Constant from 'utils/Constants';
import CustomAlert from 'utils/CustomAlert';
import { checkErrorFormToOpenCollapse, setFieldValues } from 'utils/Helper';
import { dismissToast, errorToast, loadingToastId, successToast } from 'utils/Toast';
import { CustomFormItemText, CustomFormItemInteger, CustomFormItemIntegerLabelPercentage, CustomFormItemTextArea, CustomFormItemSwitch, CustomFormItemNumber } from 'components/Common/CustomFormItem';


export default function ManageOrg() {

    const { orgId } = useParams();
    useTopbarTitleHooks(`${orgId ? "Edit" : "Create"} Organization`);
    const { isLoading: isLoadingSave, createOrg, updateOrg } = useOrgRepo(false);
    const { isLoading: isLoadingGetById, getOrgById } = useOrgRepo(false);
    const [dataField, setDataField] = useAtom(organizationAtom)


    const [form] = Form.useForm();
    const navigate = useNavigate();
    const [activeKey, setActiveKey] = useState(['1', '2']);
    const [errorList, setErrorList] = useState(false)

    const isLoadingPrivilege = useRef(true)
    const hasPrivilegePage = useRef(true)
    const { isHasPrivilege } = useAccessPermissionHook()

    // default kategori (bisa Anda ambil dari API juga)
    const DEFAULT_FTE_CATEGORIES = [
        { key: 1, category: 'Overload', min: 1.3, max: 1.5 },
        { key: 2, category: 'Underload', min: 0, max: 0.699 },
        { key: 3, category: 'Normal', min: 0.7, max: 1.2999 },
    ];

    const [fteCategories, setFteCategories] = useState(DEFAULT_FTE_CATEGORIES);
    const defaultFteRef = useRef(DEFAULT_FTE_CATEGORIES); // untuk reset ke default


    // Hapus 1 row
    const handleDelete = (key) => {
        setFteCategories(prev => prev.filter(r => r.key !== key));
    };

    // Batalkan row baru (hapus jika memang baru)
    const handleCancelNew = (key) => {
        setFteCategories(prev => prev.filter(r => r.key !== key));
    };

    // Opsional: bersihkan semua row kosong (kategori, min, max kosong)
    const removeEmptyRows = () => {
        setFteCategories(prev =>
            prev.filter(r => r.category?.trim() || r.min !== null || r.max !== null)
        );
    };

    // Reset ke default
    const resetToDefault = () => {
        setFteCategories(defaultFteRef.current);
    };



    const columns = [
        {
            title: 'Kategori',
            dataIndex: 'category',
            render: (text, record, index) => (
                <Input
                    value={text}
                    onChange={(e) => {
                        const newData = [...fteCategories];
                        newData[index].category = e.target.value;
                        // jika user mengubah, anggap bukan row baru lagi
                        newData[index].isNew = false;
                        setFteCategories(newData);
                    }}
                />
            ),
        },
        {
            title: 'Nilai Min',
            dataIndex: 'min',
            width: 180,
            render: (val, record, index) => (
                <InputNumber
                    value={val}
                    step={0.001}
                    style={{ width: '100%' }}
                    onChange={(v) => {
                        const newData = [...fteCategories];
                        newData[index].min = v;
                        newData[index].isNew = false;
                        setFteCategories(newData);
                    }}
                />
            ),
        },
        {
            title: 'Nilai Max',
            dataIndex: 'max',
            width: 180,
            render: (val, record, index) => (
                <InputNumber
                    value={val}
                    step={0.001}
                    style={{ width: '100%' }}
                    onChange={(v) => {
                        const newData = [...fteCategories];
                        newData[index].max = v;
                        newData[index].isNew = false;
                        setFteCategories(newData);
                    }}
                />
            ),
        },
        {
            title: 'Aksi',
            dataIndex: 'action',
            width: 160,
            render: (_, record) => (
                <Space>
                    {record.isNew ? (
                        // Row baru → tampilkan "Batalkan"
                        <Button
                            icon={<CloseOutlined />}
                            onClick={() => handleCancelNew(record.key)}
                        >
                            Batalkan
                        </Button>
                    ) : (
                        // Row lama/hasil edit → tampilkan "Hapus" dengan konfirmasi
                        <Popconfirm
                            title="Hapus kategori ini?"
                            okText="Ya"
                            cancelText="Tidak"
                            onConfirm={() => handleDelete(record.key)}
                        >
                            <Button danger icon={<DeleteOutlined />}>Hapus</Button>
                        </Popconfirm>
                    )}
                </Space>
            ),
        },
    ];

    // --- handler tambah kategori baru ---
    const handleAddCategory = () => {
        const newKey = fteCategories.length + 1;
        setFteCategories([
            ...fteCategories,
            { key: newKey, category: '', min: null, max: null },
        ]);
    };

    const fetchCheckPrivilege = async () => {

        loadingToastId(Constant.message.loading, PRIVILEGE_ID.AddEditOrg)
        dismissToast(PRIVILEGE_ID.AddEditOrg)
        if (orgId) {
            await fetchGetOrgById(orgId);
        }

        isLoadingPrivilege.current = false
    }

    const fetchGetOrgById = async (id) => {
        let response = await getOrgById({ orgId: id });
        if (response?.detail == 'OK' && response?.status === 200) {
            let data = response?.data;

            setFieldValues(form, "orgName", data?.orgName);
            setFieldValues(form, "orgContactNo", data?.orgContactNo);
            setFieldValues(form, "orgContactPerson", data?.orgContactPerson);
            // setFieldValues(form, "orgEfficiencyFactor", data?.orgEfficiencyFactor);
            setFieldValues(form, "orgEfficiencyFactor", Number(data?.orgEfficiencyFactor));
            setFieldValues(form, "orgAddress", data?.orgAddress);
            setFieldValues(form, "status", String(data?.status).toLowerCase() === "true");

            setDataField({
                orgId: data?.orgId,
                orgName: data?.orgName,
                orgContactNo: data?.orgContactNo,
                orgContactPerson: data?.orgContactPerson,
                // orgEfficiencyFactor: data?.orgEfficiencyFactor,
                orgEfficiencyFactor: Number(data?.orgEfficiencyFactor),
                orgAddress: data?.orgAddress,
                status: data?.status
            });
        }
    };

    const validateList = () => {
        debugger
        const isValid =
            dataField?.orgName &&
            dataField?.orgContactPerson &&
            dataField?.orgContactNo &&
            dataField?.orgEfficiencyFactor !== null &&
            !isNaN(Number(dataField?.orgEfficiencyFactor)); // ✅ periksa angka valid

        if (!isValid) {
            setErrorList(true);
            return false;
        }

        setErrorList(false);
        return true;

    }

    const doSubmit = async () => {
        debugger
        const payloadData = {
            orgId: dataField?.orgId,
            orgName: dataField?.orgName,
            orgContactNo: dataField?.orgContactNo,
            orgContactPerson: dataField?.orgContactPerson,
            orgEfficiencyFactor: dataField?.orgEfficiencyFactor,
            orgAddress: dataField?.orgAddress,
            status: dataField?.status,
        };

        loadingToastId(Constant.message.savingData, payloadData);

        let response;
        if (orgId) {
            response = await updateOrg(payloadData);
        } else {
            response = await createOrg(payloadData);
        }

        dismissToast(payloadData);

        if (response?.detail == 'OK' && response?.status === 200) {
            successToast(Constant.message.saveDataSuccess);
            navigate(APP_ROUTE_PREFIX + "/" + RouteConstant.organization.org.index, { replace: true });
        } else {
            errorToast(Constant.message.errorSave);
        }

    }

    const validateSubmit = () => {
        form.validateFields()
            .then((values) => {
                if (validateList()) {
                    doSubmit()
                } else {
                    errorToast(Constant.message.errorForm)
                }
            })
            .catch((errorInfo) => {
                errorToast(Constant.message.errorForm)
                if (checkErrorFormToOpenCollapse(errorInfo?.errorFields, [])) {
                    setActiveKey(prev => ([...new Set([...prev, '1'])]));
                }
                if (!validateList()) {
                    setActiveKey(prev => ([...new Set([...prev, '2'])]));
                }
            });
    }

    const handleFormSubmit = (e) => {
        e.preventDefault()
        CustomAlert.confirm({
            title: "Submit Organization!",
            description: `Are you sure you want to submit this data?`,
            confirmButtonText: "Yes",
            cancelButtonText: "No",
            type: "primary",
            onConfirmButton: () => validateSubmit()
        });
    }

    useEffect(() => {
        fetchCheckPrivilege()
        // return () => {
        //     setDataField(INIT_ORG);
        // };
        if (!orgId) {
            debugger
            setDataField(INIT_ORG); // ✅ akan bawa status = 'true'
            form.setFieldsValue({ status: true }); // ✅ sinkron dengan default
        } else {
            fetchGetOrgById(orgId); // jika edit
        }
    }, [orgId])

    return (
        <div>
            <Card>
                <Form
                    form={form}
                    layout="horizontal"
                    labelCol={{ span: 6 }}
                    wrapperCol={{ span: 18 }}
                    labelAlign="left"

                >
                    <Row gutter={16}>
                        <Col span={24}>
                            <CustomFormItemText
                                nameFormItem="orgName"
                                label="Name"
                                value={dataField?.orgName}
                                onChange={(e) => setDataField(prev => ({ ...prev, orgName: e.target.value }))}
                                rules={[{ required: true }]}
                                placeholder="Fill in Org Name"
                            // disabled={!!orgId} // ✅ akan true saat mode Edit
                            />
                        </Col>

                        <Col span={24}>
                            <CustomFormItemTextArea
                                nameFormItem="orgAddress"
                                label="Address"
                                value={dataField?.orgAddress}
                                onChange={(e) => setDataField(prev => ({ ...prev, orgAddress: e.target.value }))}
                                placeholder="Fill in Address"
                            />
                        </Col>


                        <Col span={24}>
                            <CustomFormItemText
                                nameFormItem="orgContactPerson"
                                label="Contact Person"
                                value={dataField?.orgContactPerson}
                                onChange={(e) => setDataField(prev => ({ ...prev, orgContactPerson: e.target.value }))}
                                rules={[{ required: true }]}
                                placeholder="Fill in Org Contact Person"
                            />
                        </Col>

                        <Col span={24}>
                            <CustomFormItemText
                                nameFormItem="orgContactNo"
                                label="Contact No"
                                value={dataField?.orgContactNo}
                                onChange={(e) => setDataField(prev => ({ ...prev, orgContactNo: e.target.value }))}
                                rules={[{ required: true }]}
                                placeholder="e.g. +6281234567890"
                            />
                        </Col>

                        <Col span={24}>
                            <CustomFormItemIntegerLabelPercentage
                                nameFormItem="orgEfficiencyFactor"
                                label="Efficiency Factor"
                                // value={dataField?.orgEfficiencyFactor}
                                onChange={(value) => setDataField(prev => ({ ...prev, orgEfficiencyFactor: value }))}
                                rules={[
                                    { required: true, message: 'Please enter Efficiency Factor' },
                                    {
                                        type: 'number',
                                        min: 65,
                                        max: 100,
                                        message: 'Efficiency Factor must be between 65 and 100',
                                    },
                                ]}
                                min={1}
                                max={100}
                                placeholder="Enter Efficiency Factor"
                                suffix="%"
                            />
                        </Col>

                        <Col span={24}>
                            <CustomFormItemSwitch
                                nameFormItem="status"
                                label="Is Active"
                                value={String(dataField?.status).toLowerCase() === 'true'} // ✅ konversi ke boolean
                                onChange={(checked) =>
                                    setDataField(prev => ({
                                        ...prev,
                                        status: checked.toString() // simpan tetap sebagai string 'true' / 'false'
                                    }))
                                }
                            // onChange={(checked) => setDataField(prev => ({ ...prev, status: checked }))}
                            />
                        </Col>

                        <Col span={24}>
                            <h4>Setting Parameter FTE Kategori</h4>

                            <Table
                                columns={columns}
                                dataSource={fteCategories}
                                pagination={false}
                                bordered
                                size="small"
                                rowKey="key"
                            />

                            <Space style={{ marginTop: 10 }}>
                                <Button icon={<PlusOutlined />} onClick={handleAddCategory}>
                                    Tambah Kategori
                                </Button>
{/* 
                                <Button onClick={removeEmptyRows}>
                                    Hapus Baris Kosong
                                </Button> */}

                                <Button icon={<RollbackOutlined />} onClick={resetToDefault}>
                                    Reset Default
                                </Button>
                            </Space>
                        </Col>

                        <Col span={24} offset={6}>
                            <div style={{ display: "flex", gap: "10px" }}>
                                <ButtonBack onClick={() => navigate(APP_ROUTE_PREFIX + "/" + RouteConstant.organization.org.index)} />
                                <ButtonSubmit onClick={handleFormSubmit} loading={isLoadingSave} disabled={isLoadingSave} />
                            </div>
                        </Col>
                    </Row>
                </Form>
            </Card>
        </div>
    )

}
